import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Loader2, Search, Plus, MessagesSquare, Paperclip } from 'lucide-react';
import {
  useSupportRequests,
  useCreateSupportRequest,
  useUnreadRequests,
  useUploadDraftAttachment,
} from '../../hooks/useSupportRequests';
import {
  TYPE_LABEL,
  type RequestStatus,
  type RequestType,
  type SupportRequest,
} from '../../services/supportRequest.service';
import { StatusBadge, RelativeTime } from './requestUi';
import { PendingUploadChips, usePendingUploads } from './PendingUploads';
import { useTenantUsers } from '../../hooks/useTenantUsers';
import { useIsMobileApp } from '../../hooks/use-mobile';

const TYPE_OPTIONS: RequestType[] = [
  'data_source',
  'data_quality',
  'question',
  'change',
  'other',
];

export default function Requests() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<RequestStatus | 'open' | 'all'>('open');
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const isMobileApp = useIsMobileApp();

  const { data, isLoading } = useSupportRequests({
    status: statusFilter,
    search: search.trim() || undefined,
  });
  const { data: unread } = useUnreadRequests();
  // Only needed to name requesters on rows created before created_by_label
  // existed; cached for 10 minutes, so it costs one call per session.
  const { data: tenantUsers } = useTenantUsers();

  const records = data?.records ?? [];

  const controls = (
    <>
      <Select
        value={statusFilter}
        onValueChange={(v) => setStatusFilter(v as RequestStatus | 'open' | 'all')}
      >
        <SelectTrigger className="w-32 sm:w-44 flex-shrink-0">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="open">Open</SelectItem>
          <SelectItem value="all">All</SelectItem>
          <SelectItem value="awaiting_pliable">Awaiting Pliable</SelectItem>
          <SelectItem value="awaiting_customer">Awaiting you</SelectItem>
          <SelectItem value="in_progress">In progress</SelectItem>
          <SelectItem value="done">Done</SelectItem>
          <SelectItem value="declined">Declined</SelectItem>
        </SelectContent>
      </Select>
      <div className="relative flex-1 sm:flex-none min-w-0">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search requests"
          className="pl-8 w-full sm:w-56 text-base sm:text-sm"
        />
      </div>
      <Button onClick={() => setCreateOpen(true)} className="flex-shrink-0">
        <Plus className="h-4 w-4 sm:mr-1" />
        <span className="hidden sm:inline">New request</span>
        <span className="sr-only sm:hidden">New request</span>
      </Button>
    </>
  );

  return (
    <div
      className="w-full flex flex-col bg-background overflow-hidden"
      style={{ height: 'calc(100vh)' }}
    >
      <PageHeader
        title={
          <div className="flex items-center gap-2 min-w-0">
            <h1 className="text-xl font-semibold text-foreground truncate">
              Requests
            </h1>
            {!!unread?.total && (
              <Badge variant="default">{unread.total} unread</Badge>
            )}
          </div>
        }
        description={
          isMobileApp
            ? undefined
            : 'Everything in flight with the Pliable team, and where each one stands.'
        }
        // On a phone the filter, the search box and the New request button
        // can't share a row with the title — they drop below it instead, where
        // they get the full width. Same controls either way.
        actions={isMobileApp ? undefined : controls}
      >
        {isMobileApp && <div className="flex items-center gap-2 mt-2">{controls}</div>}
      </PageHeader>

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-20 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Loading requests
          </div>
        ) : records.length === 0 ? (
          <EmptyState
            hasFilters={statusFilter !== 'open' || !!search.trim()}
            onCreate={() => setCreateOpen(true)}
          />
        ) : (
          <ul className="divide-y divide-border">
            {records.map((req) => (
              <RequestRow
                key={req.id}
                request={req}
                unreadCount={unread?.unread?.[req.id] ?? 0}
                requesterName={requesterName(req, tenantUsers)}
                onClick={() => navigate(`/requests/${req.id}`)}
              />
            ))}
          </ul>
        )}
      </div>

      <CreateRequestDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(id) => navigate(`/requests/${id}`)}
      />
    </div>
  );
}

/**
 * Who opened the request. Prefers the label captured at creation; falls back to
 * resolving the actor id against the tenant user list for older rows.
 */
function requesterName(
  request: SupportRequest,
  tenantUsers?: { id: string; name?: string; email?: string }[],
): string | null {
  if (request.created_by_label) return request.created_by_label;
  if (!request.created_by) return null;
  const user = tenantUsers?.find((u) => u.id === request.created_by);
  return user?.name || user?.email || null;
}

function RequestRow({
  request,
  unreadCount,
  requesterName,
  onClick,
}: {
  request: SupportRequest;
  unreadCount: number;
  requesterName: string | null;
  onClick: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className="w-full text-left px-4 sm:px-6 py-4 hover:bg-accent/50 transition-colors flex items-start gap-3 sm:gap-4"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={`text-sm truncate ${
                unreadCount > 0 ? 'font-semibold text-foreground' : 'font-medium text-foreground'
              }`}
            >
              {request.title}
            </span>
            {unreadCount > 0 && (
              <Badge variant="default" className="h-5 px-1.5 text-[11px]">
                {unreadCount} new
              </Badge>
            )}
            <Badge variant="outline" className="h-5 px-1.5 text-[11px] font-normal">
              {TYPE_LABEL[request.request_type]}
            </Badge>
          </div>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {requesterName ? (
              <>
                Opened by <span className="font-medium">{requesterName}</span>
              </>
            ) : (
              'Opened'
            )}{' '}
            <RelativeTime iso={request.created_at} />
          </p>
          {request.last_message_preview && (
            <p className="text-sm text-muted-foreground truncate mt-1">
              {request.last_message_author_label && (
                <>
                  <span
                    className={
                      request.last_message_author_type === 'pliable'
                        ? 'font-medium text-blue-700 dark:text-blue-400'
                        : 'font-medium text-foreground'
                    }
                  >
                    {request.last_message_author_label}
                  </span>
                  {': '}
                </>
              )}
              {request.last_message_preview}
            </p>
          )}
        </div>
        {/* At phone width the title needs every pixel; the status is the one
            thing here worth the space. */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <span className="text-xs text-muted-foreground hidden sm:flex items-center gap-1">
            <MessagesSquare className="h-3.5 w-3.5" />
            {request.message_count}
          </span>
          <StatusBadge status={request.status} />
          <span className="text-xs text-muted-foreground w-24 text-right hidden sm:inline">
            <RelativeTime iso={request.last_message_at || request.created_at} />
          </span>
        </div>
      </button>
    </li>
  );
}

function EmptyState({
  hasFilters,
  onCreate,
}: {
  hasFilters: boolean;
  onCreate: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center px-6">
      <Paperclip className="h-8 w-8 text-muted-foreground mb-3" />
      <p className="text-sm font-medium text-foreground">
        {hasFilters ? 'No requests match those filters' : 'No open requests'}
      </p>
      <p className="text-sm text-muted-foreground mt-1 max-w-md">
        {hasFilters
          ? 'Try widening the status filter or clearing the search.'
          : 'Start one when you need a data source connected, something changed, or a number checked.'}
      </p>
      {!hasFilters && (
        <Button className="mt-4" onClick={onCreate}>
          <Plus className="h-4 w-4 mr-1" />
          New request
        </Button>
      )}
    </div>
  );
}

function CreateRequestDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated: (id: string) => void;
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<RequestType>('other');
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const createRequest = useCreateSupportRequest();
  // The request doesn't exist yet, so files go up as drafts and are bound to it
  // when it's opened.
  const uploadDraft = useUploadDraftAttachment();
  const files = usePendingUploads(uploadDraft.mutateAsync);

  const canSubmit = useMemo(
    () => title.trim().length > 2 && !files.uploading && !files.failed,
    [title, files.uploading, files.failed],
  );

  const reset = () => {
    setTitle('');
    setDescription('');
    setType('other');
    files.clear();
  };

  // Every way out of the dialog goes through here, so reopening it never shows
  // the last attempt's title or files.
  const close = () => {
    reset();
    onOpenChange(false);
  };

  const submit = async () => {
    if (!canSubmit) return;
    const created = await createRequest.mutateAsync({
      title: title.trim(),
      description: description.trim(),
      request_type: type,
      attachment_ids: files.attachmentIds,
    });
    close();
    onCreated(created.id);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => (v ? onOpenChange(true) : close())}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>New request</DialogTitle>
          <DialogDescription>
            This starts a thread with the Pliable team.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="request-title">What do you need?</Label>
            <Input
              id="request-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Connect our Workday data"
              autoFocus
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="request-type">Type</Label>
            <Select value={type} onValueChange={(v) => setType(v as RequestType)}>
              <SelectTrigger id="request-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TYPE_OPTIONS.map((t) => (
                  <SelectItem key={t} value={t}>
                    {TYPE_LABEL[t]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div
            className={`space-y-1.5 rounded-md ${dragging ? 'bg-accent/40' : ''}`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              if (e.dataTransfer.files?.length) files.addFiles(e.dataTransfer.files);
            }}
          >
            <Label htmlFor="request-description">Details</Label>
            <Textarea
              id="request-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Anything that helps us pick this up — which system, which numbers look off, who to talk to. Drop files here to attach them."
              rows={5}
            />
            <div className="pt-1">
              <PendingUploadChips pending={files.pending} onRemove={files.remove} />
              {files.failed && (
                <p className="text-xs text-destructive mb-2">
                  Remove the files that didn't upload, or attach them again.
                </p>
              )}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => {
                  if (e.target.files?.length) files.addFiles(e.target.files);
                  e.target.value = '';
                }}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="-ml-2 text-muted-foreground"
                onClick={() => fileInputRef.current?.click()}
              >
                <Paperclip className="h-4 w-4 mr-1" />
                Attach files
              </Button>
            </div>
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={close}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit || createRequest.isPending}>
            {createRequest.isPending && (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            )}
            Open request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
